import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import AdminRichTextEditor from '../../components/admin/AdminRichTextEditor'
import {
  AdminApi,
  AdminTestResultDetail,
  AdminTestResultGradingStatus,
  AdminTestResultQuestion,
} from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

type EssayGradeDraft = {
  point: number
  mentorComment: string
}

const gradingBadgeClass = (status: AdminTestResultGradingStatus) => {
  if (status === 'pending') return 'bg-amber-100 text-amber-800'
  if (status === 'graded') return 'bg-green-100 text-green-800'
  return 'bg-slate-100 text-slate-700'
}

const gradingBadgeLabel = (status: AdminTestResultGradingStatus) => {
  if (status === 'pending') return 'Chưa chấm'
  if (status === 'graded') return 'Đã chấm'
  return 'Tự chấm'
}

const formatDuration = (seconds: number | null) => {
  if (seconds == null || seconds < 0) return '-'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m} phút ${s} giây`
}

const isEssay = (q: AdminTestResultQuestion) => q.questionType.toUpperCase() === 'ESSAY'
const isChoice = (q: AdminTestResultQuestion) => q.questionType.toUpperCase() === 'CHOICE'

const AdminTestResultDetailPage = () => {
  const { resultId } = useParams<{ resultId: string }>()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [detail, setDetail] = useState<AdminTestResultDetail | null>(null)
  const [essayGrades, setEssayGrades] = useState<Record<number, EssayGradeDraft>>({})

  const loadDetail = async () => {
    const id = Number(resultId)
    if (!Number.isInteger(id) || id < 1) {
      toast.error('ID kết quả không hợp lệ')
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const res = await AdminApi.getTestResult(id)
      if (!res.success || !res.data) {
        toast.error(res.message || 'Không tải được chi tiết kết quả')
        setDetail(null)
        return
      }
      setDetail(res.data)
      const drafts: Record<number, EssayGradeDraft> = {}
      for (const q of res.data.questions) {
        if (!isEssay(q)) continue
        drafts[q.answerId] = {
          point: q.point ?? 0,
          mentorComment: q.mentorComment || '',
        }
      }
      setEssayGrades(drafts)
    } catch {
      toast.error('Không tải được chi tiết kết quả')
      setDetail(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultId])

  const essayQuestions = useMemo(
    () => (detail?.questions || []).filter(isEssay),
    [detail]
  )

  const updateEssayPoint = (answerId: number, maxPoint: number, raw: string) => {
    const parsed = Number(raw)
    const point = Number.isFinite(parsed)
      ? Math.max(0, Math.min(maxPoint, parsed))
      : 0
    setEssayGrades((prev) => ({
      ...prev,
      [answerId]: {
        point,
        mentorComment: prev[answerId]?.mentorComment || '',
      },
    }))
  }

  const updateEssayComment = (answerId: number, html: string) => {
    setEssayGrades((prev) => ({
      ...prev,
      [answerId]: {
        point: prev[answerId]?.point ?? 0,
        mentorComment: html,
      },
    }))
  }

  const handleSaveGrades = async () => {
    if (!detail || essayQuestions.length === 0) return

    for (const q of essayQuestions) {
      const draft = essayGrades[q.answerId]
      if (!draft) continue
      if (draft.point < 0 || draft.point > q.maxPoint) {
        toast.error(`Điểm câu #${q.sort} phải trong khoảng 0–${q.maxPoint}`)
        return
      }
    }

    setSaving(true)
    try {
      const grades = essayQuestions.map((q) => ({
        answerId: q.answerId,
        point: essayGrades[q.answerId]?.point ?? q.point ?? 0,
        mentorComment: essayGrades[q.answerId]?.mentorComment || undefined,
      }))
      const res = await AdminApi.gradeTestResult(detail.id, { grades })
      if (res.success) {
        toast.success(res.message || 'Đã lưu điểm chấm')
        if (res.data?.questions) {
          setDetail(res.data)
          const drafts: Record<number, EssayGradeDraft> = {}
          for (const q of res.data.questions) {
            if (!isEssay(q)) continue
            drafts[q.answerId] = {
              point: q.point ?? 0,
              mentorComment: q.mentorComment || '',
            }
          }
          setEssayGrades(drafts)
        } else {
          void loadDetail()
        }
      } else {
        toast.error(res.message || 'Lưu điểm thất bại')
      }
    } catch {
      toast.error('Không gọi được API chấm điểm')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="text-m2-on-surface-variant">Đang tải...</div>
  }

  if (!detail) {
    return (
      <div className="space-y-3">
        <p className="text-m2-on-surface-variant">Không tìm thấy kết quả test.</p>
        <Link to="/admin/test-results" className="text-m2-primary hover:underline">
          ← Quay lại danh sách
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/admin/test-results"
            className="text-sm text-m2-primary hover:underline"
          >
            ← Quay lại danh sách
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-m2-on-surface">
            {detail.paperName || `Kết quả #${detail.id}`}
          </h1>
          <p className="mt-1 text-sm text-m2-on-surface-variant">
            {detail.courseName || 'Không gắn khóa học'} · Nộp lúc{' '}
            {formatDbDateTimeVi(detail.savedAt || detail.createdAt)} · Thời gian làm:{' '}
            {formatDuration(detail.testedSecond)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex rounded px-2 py-1 text-sm font-medium ${gradingBadgeClass(detail.gradingStatus)}`}
          >
            {gradingBadgeLabel(detail.gradingStatus)}
          </span>
          <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container px-4 py-2 text-right">
            <div className="text-lg font-semibold text-m2-on-surface">
              {detail.point}/{detail.maxPoint}
            </div>
            <div className="text-sm text-m2-on-surface-variant">
              {detail.percent}% · Đúng {detail.correctCount} · Sai {detail.wrongCount}
              {detail.pendingCount > 0 ? ` · Chờ ${detail.pendingCount}` : ''}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-m2-on-surface-variant">
          Học viên
        </h2>
        <div className="mt-2 grid gap-1 text-sm">
          <div className="font-medium text-m2-on-surface">{detail.student.fullName || '-'}</div>
          <div className="text-m2-on-surface-variant">{detail.student.email || '-'}</div>
          <div className="text-m2-on-surface-variant">
            {detail.student.zaloUrl && detail.student.phone ? (
              <>
                <a
                  href={detail.student.zaloUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-m2-primary hover:underline"
                >
                  {detail.student.phone}
                </a>
                <span className="ml-2 text-xs text-m2-on-surface-variant">(Chat Zalo)</span>
              </>
            ) : (
              detail.student.phone || '-'
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {detail.questions.map((q) => {
          const essay = isEssay(q)
          const choice = isChoice(q)
          const draft = essayGrades[q.answerId]

          return (
            <div
              key={q.answerId}
              className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-m2-on-surface">
                  Câu {q.sort}
                  <span className="ml-2 font-normal text-m2-on-surface-variant">
                    ({q.questionType || '-'}) · {q.point}/{q.maxPoint} điểm · {q.status}
                  </span>
                </div>
              </div>

              <div
                className="prose prose-sm mt-3 max-w-none rounded border border-m2-outline-variant bg-white p-3 text-slate-900 prose-headings:text-slate-900 prose-p:text-slate-800 prose-strong:text-slate-900 prose-li:text-slate-800 prose-a:text-blue-700"
                dangerouslySetInnerHTML={{ __html: q.questionContent || '' }}
              />

              {choice && (
                <ul className="mt-3 space-y-2">
                  {q.answers.map((opt) => {
                    const selected = q.selectedAnswerIds.includes(opt.id)
                    let optClass =
                      'rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900'
                    if (opt.isCorrect && selected) {
                      optClass =
                        'rounded border border-green-500 bg-green-50 px-3 py-2 text-sm text-green-900'
                    } else if (opt.isCorrect) {
                      optClass =
                        'rounded border border-green-400 bg-green-50/60 px-3 py-2 text-sm text-green-800'
                    } else if (selected) {
                      optClass =
                        'rounded border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-800'
                    }
                    return (
                      <li key={opt.id} className={optClass}>
                        <div className="flex flex-wrap items-center gap-2">
                          {selected && (
                            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800">
                              Đã chọn
                            </span>
                          )}
                          {opt.isCorrect && (
                            <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                              Đáp án đúng
                            </span>
                          )}
                        </div>
                        <div
                          className="mt-1 prose prose-sm max-w-none text-slate-900 prose-p:text-slate-800"
                          dangerouslySetInnerHTML={{ __html: opt.content || '' }}
                        />
                      </li>
                    )
                  })}
                </ul>
              )}

              {essay && (
                <div className="mt-4 space-y-4">
                  <div>
                    <div className="mb-1 text-sm font-medium text-m2-on-surface">
                      Câu trả lời học viên
                    </div>
                    <div
                      className="rounded border border-slate-300 bg-white p-3 prose prose-sm max-w-none text-slate-900 prose-headings:text-slate-900 prose-p:text-slate-800 prose-strong:text-slate-900 prose-li:text-slate-800 prose-a:text-blue-700"
                      dangerouslySetInnerHTML={{
                        __html: q.answerContent || '<p><em>Không có nội dung</em></p>',
                      }}
                    />
                  </div>

                  {q.essayAnswer ? (
                    <div>
                      <div className="mb-1 text-sm font-medium text-m2-on-surface">
                        Gợi ý đáp án
                      </div>
                      <div
                        className="rounded border border-dashed border-emerald-300 bg-emerald-50 p-3 prose prose-sm max-w-none text-emerald-950 prose-p:text-emerald-950 prose-strong:text-emerald-950"
                        dangerouslySetInnerHTML={{ __html: q.essayAnswer }}
                      />
                    </div>
                  ) : null}

                  <div className="grid gap-4 md:grid-cols-[160px_1fr]">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-m2-on-surface">
                        Điểm (0–{q.maxPoint})
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={q.maxPoint}
                        step={0.5}
                        className="w-full rounded border border-m2-outline px-3 py-2 text-base"
                        value={draft?.point ?? q.point ?? 0}
                        onChange={(e) => updateEssayPoint(q.answerId, q.maxPoint, e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-m2-on-surface">
                        Nhận xét mentor
                      </label>
                      <div className="bg-m2-surface-low">
                        <AdminRichTextEditor
                          value={draft?.mentorComment || ''}
                          onChange={(html) => updateEssayComment(q.answerId, html)}
                          placeholder="Nhập nhận xét..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!choice && !essay && q.answerContent ? (
                <div className="mt-3 rounded border border-slate-300 bg-white p-3 text-sm text-slate-900">
                  <div className="mb-1 font-medium text-slate-600">Trả lời:</div>
                  <div
                    className="prose prose-sm max-w-none text-slate-900 prose-p:text-slate-800"
                    dangerouslySetInnerHTML={{ __html: q.answerContent }}
                  />
                </div>
              ) : null}
            </div>
          )
        })}
      </div>

      {essayQuestions.length > 0 && (
        <div className="sticky bottom-4 flex justify-end">
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSaveGrades()}
            className="rounded bg-m2-primary px-4 py-2 text-base text-white hover:bg-eh-forest-shadow disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Đang lưu...' : 'Lưu điểm chấm'}
          </button>
        </div>
      )}
    </div>
  )
}

export default AdminTestResultDetailPage
